import { paymentStatementApi } from './base';

export interface Tenant {
	id: string;
	name: string;
	subdomain: string;
	schema_name: string;
	company_email: string;
	company_phone?: string;
	headquarters_address?: string;
	plan: string;
	status: string;
	max_enterprises: number;
	max_users_total: number;
	max_storage_gb: number;
	billing_email?: string;
	stripe_customer_id?: string;
	subscription_id?: string;
	logo_base64?: string | null;
	custom_domain?: string | null;
	active: boolean;
	created_at: string;
	updated_at?: string;
	provisioned_at?: string;
	last_activity_at?: string;
	metadata_json?: Record<string, any>;
	// What GET /tenant/config returns (Tenant.to_dict names it `metadata`).
	metadata?: {
		features?: string[];
		microsoft_tenant_ids?: string[];
		[key: string]: unknown;
	} | null;
	// Legacy fields
	billing_id?: string;
	data_region?: string;
}

export interface TenantsResponse {
	success: boolean;
	message: string;
	tenants: Tenant[];
	total: number;
}

export interface TenantResponse {
	success: boolean;
	message: string;
	tenant: Tenant;
}

export interface CreateTenantRequest {
	name: string;
	subdomain: string;
	company_email: string;
	enterprise_id?: string;
	company_phone?: string;
	headquarters_address?: string;
	plan?: string;
	status?: string;
	max_enterprises?: number;
	max_users_total?: number;
	max_storage_gb?: number;
	billing_email?: string;
	stripe_customer_id?: string;
	subscription_id?: string;
	logo_base64?: string | null;
	custom_domain?: string;
	base_domain?: string;
	metadata?: Record<string, any>;
}

export interface UpdateTenantRequest {
	name?: string;
	subdomain?: string;
	company_email?: string;
	company_phone?: string;
	headquarters_address?: string;
	plan?: string;
	status?: string;
	max_enterprises?: number;
	max_users_total?: number;
	max_storage_gb?: number;
	billing_email?: string;
	stripe_customer_id?: string;
	subscription_id?: string;
	logo_base64?: string | null;
	custom_domain?: string;
	base_domain?: string;
	active?: boolean;
	metadata?: Record<string, any>;
}

export interface ListTenantsParams {
	active_only?: boolean;
}

export const tenantsApi = paymentStatementApi.injectEndpoints({
	endpoints: (builder) => ({
		listTenants: builder.query<TenantsResponse, ListTenantsParams | void>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				if (params?.active_only !== undefined) {
					queryParams.append('active_only', params.active_only.toString());
				}
				const queryString = queryParams.toString();
				return {
					url: `/admin/tenants${queryString ? `?${queryString}` : ''}`,
					method: 'GET',
				};
			},
			providesTags: ['Tenants'],
		}),

		getTenant: builder.query<TenantResponse, string>({
			query: (tenantId) => ({
				url: `/admin/tenants/${tenantId}`,
				method: 'GET',
			}),
			providesTags: (result, error, tenantId) => [
				{ type: 'Tenants', id: tenantId },
			],
		}),

		createTenant: builder.mutation<TenantResponse, CreateTenantRequest>({
			query: (body) => ({
				url: '/admin/tenants',
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Tenants'],
		}),

		updateTenant: builder.mutation<
			TenantResponse,
			{ tenantId: string; data: UpdateTenantRequest }
		>({
			query: ({ tenantId, data }) => ({
				url: `/admin/tenants/${tenantId}`,
				method: 'PATCH',
				body: data,
			}),
			invalidatesTags: (result, error, { tenantId }) => [
				'Tenants',
				{ type: 'Tenants', id: tenantId },
			],
		}),

		deleteTenant: builder.mutation<
			{ success: boolean; message: string },
			string
		>({
			query: (tenantId) => ({
				url: `/admin/tenants/${tenantId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Tenants'],
			// Optimistically remove the tenant from cache for all query variations
			onQueryStarted: async (tenantId, { dispatch, queryFulfilled }) => {
				// Get all cached queries and update them
				const patchResults: any[] = [];

				// Update queries with active_only=false
				const patchResult1 = dispatch(
					tenantsApi.util.updateQueryData(
						'listTenants',
						{ active_only: false },
						(draft) => {
							draft.tenants = draft.tenants.filter((t) => t.id !== tenantId);
							draft.total = draft.tenants.length;
						},
					),
				);
				patchResults.push(patchResult1);

				// Update queries with active_only=true
				const patchResult2 = dispatch(
					tenantsApi.util.updateQueryData(
						'listTenants',
						{ active_only: true },
						(draft) => {
							draft.tenants = draft.tenants.filter((t) => t.id !== tenantId);
							draft.total = draft.tenants.length;
						},
					),
				);
				patchResults.push(patchResult2);

				// Update queries with no params (undefined)
				const patchResult3 = dispatch(
					tenantsApi.util.updateQueryData('listTenants', undefined, (draft) => {
						draft.tenants = draft.tenants.filter((t) => t.id !== tenantId);
						draft.total = draft.tenants.length;
					}),
				);
				patchResults.push(patchResult3);

				try {
					await queryFulfilled;
				} catch {
					// If deletion fails, revert all optimistic updates
					patchResults.forEach((result) => result.undo());
				}
			},
		}),

		getTenantConfig: builder.query<{ success: boolean; tenant: Tenant }, void>({
			query: () => ({
				url: '/tenant/config',
				method: 'GET',
			}),
			providesTags: ['TenantConfig'],
		}),

		// Public endpoint - get tenant config by subdomain (no auth required)
		getTenantConfigBySubdomain: builder.query<
			{ success: boolean; tenant: Tenant },
			string
		>({
			query: (subdomain) => ({
				url: `/tenant/config?subdomain=${encodeURIComponent(subdomain)}`,
				method: 'GET',
			}),
			providesTags: ['TenantConfig'],
		}),

		// Public endpoint - get tenant config by tenant_id (no auth required)
		getTenantConfigById: builder.query<
			{ success: boolean; tenant: Tenant },
			string
		>({
			query: (tenantId) => ({
				url: `/tenant/config?tenant_id=${encodeURIComponent(tenantId)}`,
				method: 'GET',
			}),
			providesTags: ['TenantConfig'],
		}),
	}),
});

export const {
	useListTenantsQuery,
	useGetTenantQuery,
	useCreateTenantMutation,
	useUpdateTenantMutation,
	useDeleteTenantMutation,
	useGetTenantConfigQuery,
	useGetTenantConfigBySubdomainQuery,
	useGetTenantConfigByIdQuery,
} = tenantsApi;
